import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import P1 from '@base/P1/P1';
import { Spacings, Fonts } from '@styles/index';
import { textTruncate } from '@utils/textHelper';

import Image from '@base/Image';
import H6 from '@base/H6/H6';
import { fetchS3SignInUrl } from '@api/fetchers/offline-centres';

const StyledLabel = styled.label`
  padding: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_14B};
  border: 1px solid var(--color-i-gray-14);
  border-radius: ${Spacings.SPACING_2B};
  display: flex;
  align-items: center;
  cursor: pointer;

  ${({ error }) => (error ? 'border: 1px solid var(--color-i-red)' : null)}
`;

const UploadStatusCard = styled.div`
  margin-top: ${Spacings.SPACING_3B};
  background-color: var(--color-base-1);
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_4B};
  padding-right: ${Spacings.SPACING_6B};
  border: 1px solid var(--color-i-gray-14);
  border-radius: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_14B};
`;

const Input = styled.input`
  display: none;
`;

const UploadTextContainer = styled.div`
  text-align: center;
  margin-top: ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_8B};
`;

const StyledH6 = styled(H6)`
  display: inline-block;
  color: var(--color-text-secondary);
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P1};
  margin-left: ${Spacings.SPACING_2B};
`;

const FileDetailsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  justify-content: space-between;
`;

const FileDetailsWrapper = styled.div`
  display: flex;
`;

const StyledP1 = styled(P1)`
  margin-bottom: ${Spacings.SPACING_1B};
  margin-left: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.SEMIBOLD};
`;

const StyledUploadP1 = styled(P1)`
  cursor: pointer;
  color: var(--color-i-blue);
`;

const doNothing = () => {};

const FileUploader = ({ onOptionClick, value, imageType, error }) => {
  const [file, setFile] = useState('');

  useEffect(() => {
    setFile(value);
  }, [value]);
  const [statusText, setStatusText] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(true);

  const handleImageCall = async (tempFile) => {
    setStatusText('Uploading...');
    setUploadSuccess(true);
    const fileName = tempFile.name;
    let fileExtension = `image/${fileName
      .split('.')
      .slice(-1)[0]
      .toLowerCase()}`;
    if (fileExtension.includes('jpg')) {
      fileExtension = 'image/jpeg';
    }
    const response = await fetchS3SignInUrl({
      fileName,
      type: 3,
      contentType: fileExtension
    }).then((res) => {
      if (!res?.public_url) {
        setStatusText('Error uploading. <b>RETRY</b>');
        setUploadSuccess(true);
        onOptionClick(res?.error?.message);
      }
      return res;
    });

    if (!response?.signed_url) {
      setStatusText('');
    } else {
      await fetch(response.signed_url, {
        method: 'PUT',
        body: tempFile,
        headers: {
          'content-type': tempFile.type
        }
      }).then((res) => {
        if (res.ok) {
          const updatedRes = {
            size: tempFile.size,
            ...(response || {}),
            name: tempFile.name
          };
          onOptionClick(updatedRes);

          setStatusText('');
          setUploadSuccess(true);
        } else {
          setStatusText('Error uploading. <b>RETRY</b>');
          setUploadSuccess(false);
          onOptionClick(res?.error);
        }
      });
    }
  };

  const handleImageUpload = async (e) => {
    const tempFile = e.target.files[0];
    if (!tempFile) return;
    setFile(tempFile);
    handleImageCall(tempFile);
  };

  const handleRetry = () => {
    handleImageCall(file);
  };

  const handleCrossClick = () => {
    onOptionClick(null);
    setFile('');
    setStatusText('');
    setUploadSuccess(true);
  };

  const hiddenFileInput = useRef(null);
  return (
    <>
      {file ? (
        <UploadStatusCard>
          <FileDetailsContainer>
            <FileDetailsWrapper>
              <Image
                src="offline-centres/postpayment/uploaded-image.svg"
                width={24}
                height={24}
                alt="upload-image"
              />
              <StyledP1>{textTruncate(file.name, 35) || 'image.svg'}</StyledP1>
            </FileDetailsWrapper>
            {statusText ? (
              <StyledUploadP1
                dangerouslySetInnerHTML={{
                  __html: 'Uploading...'
                }}
                onClick={uploadSuccess ? doNothing : handleRetry}
              />
            ) : null}
            {uploadSuccess && (
              <Image
                src="offline-centres/postpayment/delete.svg"
                width={24}
                height={24}
                alt="upload-image"
                onClick={handleCrossClick}
              />
            )}
          </FileDetailsContainer>
        </UploadStatusCard>
      ) : (
        <StyledLabel error={error} htmlFor={`uploadFile-${imageType}`}>
          <Image
            src="offline-centres/postpayment/upload-icon.svg"
            width={24}
            height={24}
            alt="upload-image"
          />
          <UploadTextContainer>
            <StyledH6>Upload from device</StyledH6>
          </UploadTextContainer>
          <Input
            id={`uploadFile-${imageType}`}
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e)}
            ref={hiddenFileInput}
          />
        </StyledLabel>
      )}
    </>
  );
};

FileUploader.propTypes = {
  onOptionClick: PropTypes.func.isRequired,
  imageType: PropTypes.number.isRequired
};

export default FileUploader;
